/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import React from 'react';
import _ from 'lodash';
import { MutateOptions, QueryClient } from '@tanstack/react-query';
import { ParentRecordState } from '.';
import { Record } from '../editor/record.types';
import { KEYS } from '../hooks/helpers/instructions/basic-task';
import { Option } from './task-recurrence-dialog';

export type SystemTypeCopyTypes = {
  create: string;
  edit: string;
  positive: string;
  negative: string;
  mutate: string
}

interface RecordScheduleHandler {
  option: Option | undefined;
  setTaskRecurrenceOptions: React.Dispatch<React.SetStateAction<boolean>>;
  currentRecord: Record<object>;
}

interface RecordEditorSystemTypeCopy {
  issue: SystemTypeCopyTypes;
  task: SystemTypeCopyTypes;
  default: SystemTypeCopyTypes;
}

export const handleParentRecordChanges = async (
  // @ts-ignore
  createdRecord: Record<string, any> | undefined,
  // @ts-ignore
  cleanCurrentRecord: Record<string, any>,
  parentRecordState: ParentRecordState,
  setAllowCreateRecord: (state: boolean) => void,
  // eslint-disable-next-line max-len
  mutateAsync: (variables: Record<object>, options?: MutateOptions<any, unknown, Record<object>, unknown> | undefined) => Promise<any>,
  queryClient?: QueryClient,
): Promise<void> => {
  const isMultipleRecordLinkage = parentRecordState.allowMultiple;

  if (!isMultipleRecordLinkage) {
    // Force an update during mutation since  the parent will always exist
    setAllowCreateRecord(false);
    const isNewParentAssignment = !_.isEmpty(parentRecordState.newParentRecord)
      && parentRecordState?.currentParentRecord?.id !== parentRecordState?.newParentRecord?.id;
    const isParentAssignmentChange = !_.isEmpty(parentRecordState.currentParentRecord)
      && parentRecordState?.currentParentRecord?.id !== parentRecordState?.newParentRecord?.id;

    if (isNewParentAssignment) {
      const parentRecord = parentRecordState?.newParentRecord;
      // add new children to new parent record
      const newChildrenRecords = parentRecord?.form_values.ccc1 || [];
      newChildrenRecords.push({
        record_id: !isParentAssignmentChange && (createdRecord ?? false)
          ? createdRecord?.id
          : cleanCurrentRecord?.id,
      });
      await mutateAsync({
        ...parentRecord,
        form_values: {
          ...parentRecord?.form_values,
          ccc1: newChildrenRecords,
        },
      });
    }

    if (isParentAssignmentChange) {
      const oldParentRecord = parentRecordState?.currentParentRecord;
      // eliminate child record from old parent
      const newChildrenRecords = oldParentRecord?.form_values.ccc1
        .filter((rc: any) => rc.record_id !== cleanCurrentRecord?.id);
      await mutateAsync({
        ...oldParentRecord,
        form_values: {
          ...oldParentRecord?.form_values,
          ccc1: newChildrenRecords,
        },
      });
    }
  }
};

const SystemTypeRecordEditorCopy: RecordEditorSystemTypeCopy = {
  issue: {
    create: 'Report issue',
    edit: 'Edit issue',
    positive: 'Save Issue',
    negative: 'Cancel',
    mutate: 'Saving...',
  },
  task: {
    create: 'Add task',
    edit: 'Edit task',
    positive: 'Save Task',
    negative: 'Cancel',
    mutate: 'Saving...',
  },
  default: {
    create: 'Add record',
    edit: 'Edit record',
    positive: 'Save record',
    negative: 'Cancel',
    mutate: 'Saving...',
  },
};

export const getRecordEditorCopy = (systemType: string): SystemTypeCopyTypes => (
  SystemTypeRecordEditorCopy[systemType]
    ? SystemTypeRecordEditorCopy[systemType]
    : SystemTypeRecordEditorCopy.default
);

export const updateRecordSchedule = (record: Record<object> | null) => (() => {
  const initialRecord: Record<object> | null = record ? { ...record } : null;

  function filterTemplateFields(currentRecord: Record<object>) {
    if (!initialRecord) {
      return [];
    }

    const formValuesTemplateFields = [
      KEYS.assignedTo,
      KEYS.title,
      KEYS.description,
      KEYS.taskType,
      KEYS.project,
      KEYS.priority,
      KEYS.appLink,
      KEYS.checkList,
    ];

    const metadataTemplateFields = [
      'assigned_to_id',
      'project_id',
    ];

    const formValuesFields = formValuesTemplateFields.filter((field: string) => {
      if (field === KEYS.appLink) {
        // Check only if the linked app changed, if the record changed but
        // not the linked app, we don't want to update other tasks in the schedule
        const linkedAppPath = `form_values[${field}][0].values['ccc3']`;
        const currentRecordLinkedApp = _.get(currentRecord, linkedAppPath);
        const initialRecordLinkedApp = _.get(initialRecord, linkedAppPath);
        return !_.isEqual(currentRecordLinkedApp, initialRecordLinkedApp);
      }

      if (field === KEYS.checkList) {
        // Only check if the checklist items changed (added or removed checklist item)
        // don't check their values (checked/unchecked)
        const checkListItemsPath = `form_values[${field}][0].elements`;
        const currentRecordCheckListItems = _.get(currentRecord, checkListItemsPath);
        const initialRecordCheckListItems = _.get(initialRecord, checkListItemsPath);
        return !_.isEqual(currentRecordCheckListItems, initialRecordCheckListItems);
      }
      return !_.isEqual(currentRecord.form_values[field], initialRecord?.form_values[field]);
    });

    const metadataFields = metadataTemplateFields.filter((field: string) => (
      !_.isEqual(currentRecord[field], initialRecord![field])
    ));

    return [...formValuesFields, ...metadataFields];
  }

  return {
    rruleHasChanged(currentRecord: Record<object>) {
      if (currentRecord.record_series) {
        return (
          _.trimEnd(initialRecord?.rrule as string)
          !== _.trimEnd(currentRecord.record_series.rrule as string)
        );
      }
      return false;
    },
    handleRRule({ option, setTaskRecurrenceOptions, currentRecord }: RecordScheduleHandler) {
      const { record_series, ...restRecord } = currentRecord;
      const recordSeriesIncluded = {
        ...currentRecord,
        record_series: {
          rrule: record_series?.rrule || currentRecord.rrule,
        },
      };

      if (option) {
        setTaskRecurrenceOptions(false);
        if (option === 'singleTask') {
          return restRecord;
        }

        return recordSeriesIncluded;
      }

      if (initialRecord && !this.rruleHasChanged(currentRecord)) {
        return restRecord;
      }

      return currentRecord;
    },
    templateFieldChanges(currentRecord: Record<object>) {
      if ((_.isEqual(currentRecord, initialRecord))
        || !initialRecord // if this is false, the record is not a part of a series
        || !filterTemplateFields(currentRecord).length
      ) {
        return undefined;
      }
      return 'Update';
    },
  };
})();

export function cancelRecordSeries(record: Record<object>) {
  const hasRemovedDueDate = (record.form_values[KEYS.dueDate] === '') && record.record_series_id;

  if (hasRemovedDueDate) {
    return { ...record, record_series: { rrule: null } };
  }

  return { ...record };
}

export const formatSeries = (isBasicTask: boolean, row: Record<object>) => {
  if (isBasicTask && row.rrule && !row.record_series) {
    return { ...row, record_series: { rrule: row.rrule } };
  }

  return row;
};
